import React, { useEffect } from 'react';
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Images } from '../../../../constants/Images';
import { useBagStore } from '../../../../store/BagStore';
import { useNavigation } from '@react-navigation/native';
import { NavigationProps } from '../../../../navigation/Routes';
import { AppRoutes } from '../../../../navigation/Routes.types';
import useExtendedTermsStore from '../../../../store/ExtendedTermsStore';
import * as Progress from 'react-native-progress';
import { Colors } from '../../../../constants/Colors';
import { useIsDesktop } from '../../../../hooks/useIsDesktop';

const SIDEBAR_WIDTH = 250;

const BagModal = () => {
  const { navigate } = useNavigation<NavigationProps>();
  const isDesktop = useIsDesktop();
  const { width: windowWidth } = useWindowDimensions();

  const totalItems = useBagStore((state) => state.getTotalItems());
  const totalPrice = useBagStore((state) => state.getTotalPrice());

  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const { data: extendedTerms, loading: extendedTermsLoading, error: extendedTermsError } = useExtendedTermsStore();
  const [percentage, setPercentage] = React.useState(0);
  const [remaining, setRemaining] = React.useState(0);
  const [days, setDays] = React.useState(0);
  const [shouldRemoveBorderRadius, setShouldRemoveBorderRadius] = React.useState(false);

  useEffect(() => {
    if (totalPrice > 0 && extendedTerms && !extendedTermsLoading && !extendedTermsError) {
      const quota = extendedTerms?.value || 0;
      const calculatedPercentage = Math.min((totalPrice / quota) * 100, 100);
      setPercentage(calculatedPercentage);

      const calculatedRemaining = Math.max(quota - totalPrice, 0);
      setRemaining(calculatedRemaining);

      const calculatedDays = extendedTerms?.days || 0;
      setDays(calculatedDays);

      setShouldRemoveBorderRadius(calculatedPercentage > 0);
      setShowConfirmation(calculatedRemaining <= 0);
    } else {
      setShouldRemoveBorderRadius(false);
      setShowConfirmation(false);
    }
  }, [totalPrice, extendedTerms, extendedTermsLoading, extendedTermsError]);

  const isWeb = Platform.OS === 'web';
  const contentWidth = isDesktop ? windowWidth - SIDEBAR_WIDTH : windowWidth;

  return (
    <>
      {totalItems > 0 && (
        <View
          style={[
            styles.containerModal,
            { width: isWeb ? '100%' : windowWidth },
          ]}
        >
          {isWeb ? (
            // ─── WEB: barra compacta clicável ───
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => navigate(AppRoutes.BAG)}
              style={styles.webCard}
            >
              {!extendedTermsLoading && !extendedTermsError && extendedTerms && percentage > 0 && remaining > 0 && (
                <Text style={styles.webTermsText}>
                  Faltam{' '}
                  <Text style={styles.highlightedText}>
                    {remaining.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Text>{' '}
                  para ter{' '}
                  <Text style={styles.highlightedText}>
                    {extendedTerms.days} dias para pagar!
                  </Text>
                </Text>
              )}
              {showConfirmation && (
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                  <View style={styles.checkIcon}>
                    <Text style={styles.checkIconText}>✓</Text>
                  </View>
                  <Text style={styles.webTermsText}>
                    Seu boleto será gerado com{' '}
                    <Text style={styles.highlightedText}>
                      {extendedTerms?.days} dias para pagar!
                    </Text>
                  </Text>
                </View>
              )}
              <View style={styles.webBar}>
                {percentage > 0 && remaining > 0 && (
                  <View style={[styles.webBarFill, { width: `${percentage}%` as any }]} />
                )}
                <View style={styles.webBarContent}>
                  <Image
                    source={Images.bag}
                    style={{ width: 18, height: 22, tintColor: 'white', marginRight: 8 }}
                  />
                  <Text style={styles.webBarText}>
                    {totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    {' · '}{totalItems} {totalItems > 1 ? 'itens' : 'item'}
                    {' · Ver sacola →'}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          ) : (
            // ─── MOBILE: design original ───
            <>
              {!extendedTermsLoading && !extendedTermsError && extendedTerms && percentage > 0 && remaining > 0 && (
                <View style={styles.extendedTermsCard}>
                  {remaining > 0 && (
                    <Text style={styles.extendedTermsCardText}>
                      Faltam{' '}
                      <Text style={styles.highlightedText}>
                        {remaining.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </Text>{' '}
                      para ter{' '}
                      <Text style={styles.highlightedText}>
                        {extendedTerms.days} dias para pagar!
                      </Text>
                    </Text>
                  )}
                  {(percentage > 0 && remaining > 0) && (
                    <View style={styles.progressContainer}>
                      <Progress.Bar
                        progress={percentage / 100}
                        height={10}
                        width={windowWidth - 40}
                        color={Colors.RedPallete.red}
                        borderRadius={5}
                        unfilledColor={'#fff'}
                        borderWidth={0}
                      />
                    </View>
                  )}
                </View>
              )}
              {showConfirmation && (
                <View style={styles.extendedTermsConfirmationCard}>
                  <View style={styles.checkIcon}>
                    <Text style={styles.checkIconText}>✓</Text>
                  </View>
                  <Text style={styles.extendedTermsCardText}>
                    Seu boleto será gerado com{' '}
                    <Text style={styles.highlightedText}>
                      {extendedTerms?.days} dias para pagar!
                    </Text>
                  </Text>
                </View>
              )}
              <View
                style={[
                  styles.containerFilter,
                  {
                    borderTopLeftRadius: shouldRemoveBorderRadius ? 0 : 16,
                    borderTopRightRadius: shouldRemoveBorderRadius ? 0 : 16,
                  },
                ]}
              >
                <TouchableOpacity
                  onPress={() => navigate(AppRoutes.BAG)}
                  style={{ flex: 1 }}
                >
                  <View style={styles.containerBag}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Image
                        source={Images.bag}
                        style={{ width: 22, height: 29, tintColor: 'white', marginRight: 4 }}
                      />
                      <Text style={styles.text}>
                        Ver sacola ({totalItems} {totalItems > 1 ? 'itens' : 'item'})
                      </Text>
                    </View>
                    <Text style={styles.text}>
                      {totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>
      )}
    </>
  );
};

export const styles = StyleSheet.create({
  // ─── Shared ───
  text: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  containerModal: {
    justifyContent: 'flex-end',
    position: 'absolute',
    bottom: 0,
    zIndex: 100,
  },
  highlightedText: {
    color: Colors.RedPallete.red,
    fontWeight: '600',
  },
  checkIcon: {
    backgroundColor: Colors.RedPallete.red,
    borderRadius: 50,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 5,
  },
  checkIconText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
  },

  // ─── Web ───
  webCard: {
    backgroundColor: '#f5f5f5',
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 8,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: '#ddd',
  },
  webTermsText: {
    fontSize: 12,
    color: '#333',
    textAlign: 'center',
    marginBottom: 6,
    lineHeight: 18,
  },
  webBar: {
    height: 40,
    width: '100%',
    backgroundColor: Colors.RedPallete.red,
    borderRadius: 8,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  webBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
  webBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  webBarText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'white',
  },

  // ─── Mobile ───
  containerFilter: {
    height: 60,
    width: '100%',
    backgroundColor: '#DD0B1C',
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  containerBag: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    flex: 1,
  },
  extendedTermsCard: {
    backgroundColor: '#f0f0f0',
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    minHeight: 50,
  },
  extendedTermsConfirmationCard: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    minHeight: 50,
  },
  extendedTermsCardText: {
    fontSize: 12,
    color: '#333',
    textAlign: 'center',
    marginBottom: 5,
    lineHeight: 18,
    flexWrap: 'wrap',
    paddingHorizontal: 4,
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
});

export default BagModal;
